<template>
  <div>
    <nanobar class="nanobar-component" />

    <div v-if="status === 'ready'">
      <sidebar />

      <div class="wrapper">
        <div id="app-container" class="main">
          <navbar />

          <router-view v-if="!navigationErrorCode" />
          <error v-else :code="navigationErrorCode" />
        </div>
      </div>

      <messages />
      <scroll-to-top v-if="!$store.getters.hasFloatingButton" />
    </div>

    <loading v-else-if="status === 'loading'" />
    <cannot-start v-else-if="status === 'error'" :error="error" />
  </div>
</template>

<script>
import Loading from './Loading'
import CannotStart from '@/components/page/errors/CannotStart'
import Navbar from './Navbar'
import Sidebar from './Sidebar'
import Error from '@/components/page/errors/Error'

import to from '@/modules/await-to-js'
import constants from '@/configuration/constants'
import Duration from 'duration-js'
import eventBus from '@/modules/eventBus'
import formatDate from 'vue-kerlink/dist/filters/date'

export default {
  name: 'Squeleton',

  components: { Loading, CannotStart, Navbar, Sidebar, Error },

  data () {
    return {
      status: 'loading',
      error: '',
      navigationErrorCode: 0,
      // Hide scrollToTop if true
      hasFloatingButton: false
    }
  },

  watch: {
    '$route' (from, to) {
      eventBus.$emit('progress-reset')

      this.checkRoleForThisRoute(from, to)
    }
  },

  /** Application initialization
   * - get the authenticated user
   * - then, get roles, customer and settings
   * - once all requests are done, init the Vue application
   */
  async mounted () {
    // Start loading screen
    this.status = 'loading'

    // Display error screen on 404/403 event
    eventBus.$on('request:403', () => { this.navigationErrorCode = 403 })
    eventBus.$on('request:404', () => { this.navigationErrorCode = 404 })
    eventBus.$on('request:clear', () => { this.navigationErrorCode = 0 })

    let authError
    [authError] = await to(this.$store.dispatch('getAuthenticatedUser'))
    if (authError) {
      // Display error message
      this.status = 'error'
      this.error = 'init'
      return
    }

    let error
    [error] = await to(Promise.all([
      this.$store.dispatch('getAuthenticatedUserCustomer'),
      this.$store.dispatch('getSettings'),
      this.$store.dispatch('getCapabilities'),
      // Once initialized, get application roles
      // These won't change, and doing it here will
      // help to have a nice render in profile view
      this.isRole.admin ? this.$store.dispatch('getRoles') : Promise.resolve()
    ]))
    if (error) {
      if (process.env.NODE_ENV === 'production') {
        // Display error message
        this.status = 'error'
        this.error = 'init'
        return
      }

      if (process.env.NODE_ENV === 'development') {
        // Log error message
        if (error.config) console.warn(error.config.url, error.message)
        else console.warn(error)
      }
    }

    this.initApplication()
    this.checkRoleForThisRoute()
  },

  destroyed () {
    // Clear nanobar
    const nanobars = document.getElementsByClassName('nanobar')
    for (const nanobar of nanobars) {
      nanobar.parentNode.removeChild(nanobar)
    }

    // Clean listerners
    eventBus.$off('request:403')
    eventBus.$off('request:404')
    eventBus.$off('request:clear')
  },

  methods: {
    initApplication () {
      // Check the user has at least one fleet and one cluster
      this.$store.dispatch('checkUserHasFleets')
      this.$store.dispatch('checkUserHasClusters')

      // Activate disconnection warning
      this.activateDisconnectionWarning()
    },

    checkRoleForThisRoute (to) {
      // On init, the route does not change, so we use the current route
      const route = to || this.$router.currentRoute

      // Loop on matched routes in order to check roles of this route or inherited roles from parent routes
      // until roles are found, otherwise it is considered as an open route by default
      const routeMatched = route.matched || [route]
      let routeChecked = false
      for (let i = routeMatched.length - 1; i >= 0; i--) {
        if (routeChecked) {
          return
        }

        let _route = routeMatched[i]

        // Some roles are defined => check its
        if (_route.meta.roles) {
          if (
            _route.meta.roles === '*' || // Open route
            _route.meta.roles.indexOf(this.$store.getters.authenticatedUser.role) !== -1 // Authorized route
          ) {
            routeChecked = true
            this.status = 'ready'
            return
          } else { // Unauthorized route
            routeChecked = true
            this.$router.replace('/403', () => {
              this.status = 'ready'
            })
          }
        } else if (i === 0) { // No roles are defined => check the parent, unless this is the root route => open route
          routeChecked = true
          this.status = 'ready'
          return
        }
      }
    },

    // Activate a timer that will display a disconnection warning before the expiration of the token
    activateDisconnectionWarning () {
      const expiredDate = this.$api.getTokenExpirationDate()
      // No expiration date stored => no warning
      if (!expiredDate) {
        return false
      }

      let timer = expiredDate - Date.now()
      if (timer < 0) {
        // Timer negative => disconnection date in past => logout
        this.$api.logout()
      } else if (timer < constants.disconnectionWarningTimeout) {
        // Timer < 2min => display warning now
        timer = 0
      } else {
        // Else => display 2min before disconnection
        timer = timer - constants.disconnectionWarningTimeout
      }

      setTimeout(() => {
        if (Date.now() >= expiredDate) {
          // Can happen when the user's OS session was put into standby and wakeups after the expire date
          this.$api.logout(this.$route.fullPath)
        } else {
          // Display message of disconnection anyway
          eventBus.$emit('message-warning', this.$t('squeleton:disconnectionWarning', {
            duration: new Duration(expiredDate - Date.now()).humanize(),
            hour: formatDate(expiredDate, 'hour')
          }))
        }
      }, timer)
    }
  }
}
</script>

<style scoped lang="scss">
  @import "~@/scss/variables";

  .wrapper {
    margin: $navbar-height 0 90px;

    @media (min-width: $grid-breakpoint-md) {
      margin-left: $sidebar-width;
    }
  }

  .main {
    padding: 20px;

    @media (min-width: $grid-breakpoint-xl) {
      min-width: 950px;
      max-width: 80vw;
      margin-right: auto;
      margin-left: auto;
    }
  }
</style>
